import type { AsyncData } from 'nuxt/app';
import type { FormId, UtilitiesForm } from '@utilities/types';
import type {
  AccountStatsResponse,
  UtilitiesAccountsResponse,
  UtilitiesOrdersError,
  UtilitiesOrdersPropertiesResponse,
  UtilitiesOrdersResponse,
  HoldingAgreementCreateResponse,
  HoldingAgreementPreviewResponse,
  OverlapCheckResponse,
  PackagePricesResponse,
} from '@utilities/types/responses';
import type {
  CreateOrUpdateOrderFormPayload,
  FormUploadPayload,
  UtilitiesSendReminderPayload,
  UtilitiesOrdersPropertiesPayload,
  UtilitiesQueryPayload,
  OverlapCheckPayload,
  PackagePricesPayload,
} from '@utilities/types/requests';

export default {
  accounts: {
    get(query: UtilitiesQueryPayload) {
      return useApiFetch<UtilitiesAccountsResponse>(
        useZiggy('agents.utilities.accounts.index'),
        {
          method: 'GET',
          query,
        },
      );
    },

    create() {
      return useApiFetch(useZiggy('agents.utilities.accounts.create'), {
        method: 'GET',
      });
    },

    show(id: FormId) {
      return useApiFetch(useZiggy('agents.utilities.accounts.show', id), {
        method: 'GET',
      });
    },

    store(body = {}) {
      return useApiFetch(useZiggy('agents.utilities.accounts.store'), {
        method: 'POST',
        body,
      });
    },

    destroy(body: { forms: FormId[] }) {
      return useApiFetch(useZiggy('agents.utilities.accounts.destroy'), {
        method: 'DELETE',
        body,
      });
    },

    stats(query = {}) {
      return useApiFetch<AccountStatsResponse>(
        useZiggy('agents.utilities.accounts.stats'),
        {
          method: 'GET',
          query,
        },
      );
    },
  },

  orders: {
    show(id: FormId) {
      return useApiFetch<UtilitiesOrdersResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.orders.show', id),
        {
          method: 'GET',
        },
      );
    },

    store(body: CreateOrUpdateOrderFormPayload) {
      return useApiFetch<UtilitiesOrdersResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.orders.store'),
        {
          method: 'POST',
          body,
          watch: false,
        },
      );
    },

    update(body: CreateOrUpdateOrderFormPayload, id: FormId) {
      return useApiFetch<UtilitiesOrdersResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.orders.update', id),
        {
          method: 'PUT',
          body,
          watch: false,
        },
      );
    },

    void(id: FormId) {
      return useApiFetch(useZiggy('agents.utilities.orders.void', id), {
        method: 'PATCH',
      });
    },

    properties(query: UtilitiesOrdersPropertiesPayload) {
      return useApiFetch<UtilitiesOrdersPropertiesResponse>(
        useZiggy('agents.utilities.orders.properties'),
        {
          method: 'GET',
          query,
        },
      );
    },

    send(id: FormId) {
      return useApiFetch(useZiggy('agents.utilities.orders.send', id), {
        method: 'PATCH',
      });
    },

    resend(id: FormId, body: UtilitiesSendReminderPayload) {
      return useApiFetch(useZiggy('agents.utilities.orders.resend', id), {
        method: 'PATCH',
        body,
      });
    },

    upload(body: { forms: FormUploadPayload[] }) {
      return useApiFetch(useZiggy('agents.utilities.orders.upload'), {
        method: 'POST',
        body,
      });
    },

    clone(id: FormId) {
      return useApiFetch<UtilitiesForm>(
        useZiggy('agents.utilities.orders.clone', id),
        {
          method: 'POST',
        },
      );
    },

    delete(id: FormId): AsyncData<never, any> {
      return useApiFetch(useZiggy('agents.utilities.orders.delete', id), {
        method: 'DELETE',
      });
    },

    previewLink(id: FormId) {
      return useApiFetch(useZiggy('agents.utilities.orders.preview', id));
    },

    cities() {
      return useApiFetch(useZiggy('agents.utilities.orders.cities'), {
        method: 'GET',
      });
    },

    overlapCheck(payload: OverlapCheckPayload) {
      return useApiFetch<OverlapCheckResponse>(useZiggy('agents.utilities.orders.overlap-check'), {
        method: 'POST',
        body: payload,
      });
    },

    packagePrices(payload: PackagePricesPayload) {
      return useApiFetch<PackagePricesResponse>(
        useZiggy('agents.utilities.orders.package-prices'),
        {
          method: 'POST',
          body: payload,
        },
      );
    },
  },

  holdingAgreements: {
    store(body: CreateOrUpdateOrderFormPayload) {
      return useApiFetch<HoldingAgreementCreateResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.holding-agreements.store'),
        {
          method: 'POST',
          body,
          watch: false,
        },
      );
    },

    update(id: number, body: CreateOrUpdateOrderFormPayload) {
      return useApiFetch<HoldingAgreementCreateResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.holding-agreements.update', id),
        {
          method: 'PUT',
          body,
          watch: false,
        },
      );
    },

    preview(id: number) {
      return useApiFetch<HoldingAgreementPreviewResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.holding-agreements.preview', id),
        {
          method: 'GET',
          watch: false,
        },
      );
    },

    previewUtilitiesForm(id: number) {
      return useApiFetch<HoldingAgreementPreviewResponse, UtilitiesOrdersError>(
        useZiggy('agents.utilities.holding-agreements.preview-utilities-form', id),
        {
          method: 'GET',
          watch: false,
        },
      );
    },

    void(id: number) {
      return useApiFetch(useZiggy('agents.utilities.holding-agreements.void', id), {
        method: 'PATCH',
      });
    },

    clone(id: number) {
      return useApiFetch<{
        holdingAgreement: {
          id: number;
          utilities_form_id: number;
        };
      }>(useZiggy('agents.utilities.holding-agreements.clone', id), {
        method: 'POST',
      });
    },

    send(id: number) {
      return useApiFetch(useZiggy('agents.utilities.holding-agreements.send', id), {
        method: 'PATCH',
      });
    },
  },
};
